import React, { createContext, ReactElement, ReactNode, useContext, useMemo, useState } from 'react';

// ── Public action surface (consumed by RetroMenu) ───────────────────────────

export interface RetroActionsPayload {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onExportMarkdown: () => void;
  onExportPlainText: () => void;
  canExportSelection: boolean;
  onExportSelectionMarkdown: () => void;
  onExportSelectionPlainText: () => void;
  onExportBoardAsImage: () => void;
  onExportSelectionAsImage: () => void;
  /** True when the current player is the moderator and the retro is still active. */
  canEndRetro: boolean;
  onEndRetro: () => void;
  /** True when the current player is the moderator and the retro has been ended. */
  canReopenRetro: boolean;
  onReopenRetro: () => void;
}

// ── Registration surface (consumed by CanvasSurface) ────────────────────────

interface RetroActionsRegistration {
  /** Called by CanvasSurface whenever history state changes. */
  register: (payload: RetroActionsPayload) => void;
  /** Called by CanvasSurface on unmount to reset to defaults. */
  unregister: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const defaultPayload: RetroActionsPayload = {
  canUndo: false,
  canRedo: false,
  onUndo: noop,
  onRedo: noop,
  onExportMarkdown: noop,
  onExportPlainText: noop,
  canExportSelection: false,
  onExportSelectionMarkdown: noop,
  onExportSelectionPlainText: noop,
  onExportBoardAsImage: noop,
  onExportSelectionAsImage: noop,
  canEndRetro: false,
  onEndRetro: noop,
  canReopenRetro: false,
  onReopenRetro: noop,
};

// The payload context changes on every registration; only RetroMenu consumes it.
const RetroActionsPayloadContext = createContext<RetroActionsPayload>(defaultPayload);

// The registration context holds stable callbacks and never changes after mount,
// so CanvasSurface can consume it without re-rendering when the payload updates.
const RetroActionsRegistrationContext = createContext<RetroActionsRegistration>({
  register: noop,
  unregister: noop,
});

// ── Hook for consumers (RetroMenu) ──────────────────────────────────────────

export function useRetroActions(): RetroActionsPayload {
  return useContext(RetroActionsPayloadContext);
}

// ── Hook for registrants (CanvasSurface) ────────────────────────────────────

export function useRetroActionsRegistration(): RetroActionsRegistration {
  return useContext(RetroActionsRegistrationContext);
}

// ── Provider (lives in UserApp, above AppBar and Routes) ────────────────────

export function RetroActionsProvider({ children }: { children: ReactNode }): ReactElement {
  const [payload, setPayload] = useState<RetroActionsPayload>(defaultPayload);

  // Stable for the lifetime of the provider so the registration context value
  // never changes — this is what prevents CanvasSurface from re-rendering (and
  // thus re-registering) in an infinite loop.
  const registration = useMemo<RetroActionsRegistration>(
    () => ({
      register: (p: RetroActionsPayload) => setPayload(p),
      unregister: () => setPayload(defaultPayload),
    }),
    [],
  );

  return (
    <RetroActionsRegistrationContext.Provider value={registration}>
      <RetroActionsPayloadContext.Provider value={payload}>
        {children}
      </RetroActionsPayloadContext.Provider>
    </RetroActionsRegistrationContext.Provider>
  );
}
